import { Component, Input, OnDestroy, AfterViewInit, inject } from '@angular/core'
import { combineLatest, merge, of, takeUntil } from 'rxjs'

import confetti from 'canvas-confetti'
import { CountUpModule } from 'ngx-countup'

import { DateOnly, datelib, log } from '@nx-superprep/utils'
import { ContentTile, TileComponent } from '@nx-superprep/shared'
import { RangedModuleStats, StudyService, UserStatsRange } from '@nx-superprep/backend/firebase'
import { NgStyle, NgClass, NgTemplateOutlet, AsyncPipe, PercentPipe } from '@angular/common'
import { isEqual } from 'lodash-es'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { MatButtonModule } from '@angular/material/button'
import { MatDividerModule } from '@angular/material/divider'
import { MatIconModule } from '@angular/material/icon'

@Component({
  standalone: true,
  selector: 'study-lesson-complete',
  templateUrl: './lesson-complete.component.html',
  styleUrls: ['./lesson-complete.component.scss'],
  imports: [
    NgStyle,
    NgClass,
    NgTemplateOutlet,
    AsyncPipe,
    PercentPipe,
    CountUpModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
  ],
})
export class LessonCompleteComponent extends TileComponent implements OnDestroy, AfterViewInit {

  private _data?: {
    name: string
    displayName?: string
    maturityProgress?: number
    answersRight?: number
    questions?: number
    retentionRate?: number
    studyTime?: number
    finished?: boolean
  } | undefined
  @Input()
  public get data() {
    return this._data
  }
  public set data(value) {
    if (isEqual(this._data, value)) { return }
    this._data = value
    if (this._firework) {
      this._firework({ particleCount: 100, startVelocity: 30, spread: 360, origin: { x: 0.5, y: 0.2 }})
      ?.finally(() => {
        this._firework.reset()
        this._canvas?.remove()
      })
    }
  }

  private _moduleId: string|undefined
  @Input()
  get moduleId() { return this._moduleId}
  set moduleId(id) {
    this._moduleId = id
    if (!this.moduleId) {
      this.onClose('missing module id')
    } else {
      this.getData(this.moduleId)
    }
  }

  @Input() close: (reason: 'continue'|'done'|string) => void = () => this.bsModalRef.hide()

  get isProgressBarVisible() { return false } //{ return !this._nextModuleId }
  get isNavigationVisible() { return !this._nextModuleId }

  get titleText(): string { return this.isFinished?'Great Job!':'All Done!'}
  get subtitleText(): string { return `You ${this.isFinished?'finished':'completed'} ${this.displayName}.`}
  get maturityProgress(): number { return Number(this.data?.maturityProgress) || 0 }
  get answersRight(): number { return Number(this.data?.answersRight) || 0 }
  get questions(): number { return Number(this.data?.questions) || 0}
  get retentionRate(): number { return Number(this.data?.retentionRate) || 0}
  get studyTime(): number { return Number(this.data?.studyTime) || 0 }
  get lessonId(): string { return this.data?.name ?? '' }
  get displayName(): string { return this.data?.displayName ?? '' }
  get isFinished(): boolean { return this.data?.finished ?? false }

  countUpOptions = { }
  studyTimeOptions = { decimalPlaces: 2, decimal: ':' }

  private _canvas!: HTMLCanvasElement
  private _firework!: confetti.CreateTypes
  private _nextModuleId: string | undefined

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this._firework?.reset()
  }

  onClose(reason: string | 'continue' | 'done' = 'done') {
    this.close(reason)
  }

  getData(id: string) {
    log.debug('complete', id)
    const currentModule = this.studyService.activeLesson?.planAndModules.modules[0]
    const date = new DateOnly(currentModule?.moduleId === id ? currentModule?.lastStudyDate : undefined)
    const source = this.studyService.activeLesson
    ? combineLatest([
      merge(of(this.studyService.activeLesson.planAndModules), this.studyService.getDayPlanAndModules(date)),
      merge(of(this.studyService.activeLesson.stats), this.studyService.getDailyStatsForModule(id, date))
    ])
    : combineLatest([
      this.studyService.getDayPlanAndModules(date),
      this.studyService.getDailyStatsForModule(id, date)
    ])

    source
    .pipe(takeUntil(this.onDestroy))
    .subscribe(([dayPlanAndModules, stats]) => {
      if (!dayPlanAndModules) { this.onClose('missing day plan and modules'); return }
      const [plan, modules] = [dayPlanAndModules.plan, dayPlanAndModules.modules]
      const module = this.studyService.activeLesson?.module.moduleId === id
      ? this.studyService.activeLesson?.module
      : modules.find(m => m.moduleId === id)
      if (!plan.id || !module) { this.onClose(`missing ${plan.id ? id: 'plan id'} for ${date.toISOString()}`); return }
      this.element = { content: { iconName: module.moduleIcon, title: module.moduleTitle } as ContentTile}
      this._nextModuleId = modules.find((m) => m.moduleId !== id && !m.isFinished)?.moduleId
      stats = stats ? stats : new RangedModuleStats(module.moduleId, UserStatsRange.Daily, DateOnly.toTimestamp())
      if (!stats && !this.data) { this.onClose(`missing stats and data`); return }
      const answersRight = stats.rightAcquisitionCount + stats.rightReviewCount + stats.rightPracticeCount
      const answersWrong = stats.wrongAcquisitionCount + stats.wrongReviewCount + stats.wrongPracticeCount
      const questions = answersRight + answersWrong
      const retentionRate = Math.ceil(100 * stats.rightReviewCount / ((stats.rightReviewCount + stats.wrongReviewCount) || 1))
      if (!module?.isFinished && currentModule?.moduleId === module.moduleId) { this.onClose(`not complete ${module.moduleId}`); return }
      this.data = {
        name: id,
        displayName: module.moduleTitle,
        maturityProgress: module.progress,
        answersRight,
        questions,
        retentionRate,
        studyTime: stats.studyTime,
        finished: module.isFinished
      }
    })
  }

  continueMessage() {
    return this._nextModuleId ? 'Continue to the next module?' : 'Continue to the dashboard?'
  }

  get time() {
    return (
      datelib.duration(this.studyTime, 's').minutes() +
      datelib.duration(this.studyTime, 's').seconds() / 100
    )
  }

  ngAfterViewInit(): void {
    this._canvas = document.getElementById('_confetti') as HTMLCanvasElement
    this._firework = confetti.create(this._canvas, { resize: true, useWorker: true, disableForReducedMotion: true })
  }

  readonly bsModalRef = inject(BsModalRef)
  readonly studyService = inject(StudyService)

  constructor() {
    super()
    this.rootClassName = 'lesson-complete-root-class-name'
  }
}
